/**
 * Centralized timing configuration for Puzzle Quest animations and delays
 * All values in milliseconds
 *
 * Update these values to adjust animation timing across the entire feature
 */

// ============================================================================
// BASE TIMING CONSTANTS
// ============================================================================

/**
 * Base auto-move timeout for piece animations
 * Used as foundation for other move-related timings
 */
export const AUTO_MOVE_BASE = 1000

/**
 * Extended auto-move timeout (for first move)
 * Calculated: AUTO_MOVE_BASE * 2
 */
export const AUTO_MOVE_EXTENDED = AUTO_MOVE_BASE * 2 // 2000ms

/**
 * Auto-feed timeout - time before automatically advancing to next puzzle
 */
export const AUTO_FEED_TIMEOUT = 15000

// ============================================================================
// BOARD OVERLAY TIMINGS
// ============================================================================

export const BOARD_OVERLAY = {
    /** Total duration for board overlay animation */
    totalDuration: AUTO_MOVE_EXTENDED, // 2000ms

    /** Duration for grow animation (top/bottom overlays) */
    growAnimation: AUTO_MOVE_EXTENDED, // 2000ms

    /** Duration for text fade in/out */
    textFadeInOut: AUTO_MOVE_EXTENDED, // 2000ms
} as const

// ============================================================================
// REWARD POPUP TIMINGS
// ============================================================================

export const REWARD_POPUP = {
    /** Sound effect delay for normal step completion */
    soundEffectDelayNormal: 1500,

    /** Sound effect delay for last day/cycle completion */
    soundEffectDelayLastDay: 2500,

    /** Particle burst animation timing */
    particles: {
        /** Delay before particles appear */
        delay: 1500,
        /** Duration of particle appearance animation */
        duration: 1000,
    },

    /** Frame (avatar border) animation timing */
    frame: {
        /** Delay before frame appears */
        delay: 750,
        /** Duration of frame fade in */
        duration: 1000,
    },

    /** Reward icon animation timing */
    rewardIcon: {
        /** Delay before reward icon appears */
        delay: 2000,
        /** Duration of reward icon animation */
        duration: 750,
    },

    /** Reposition animation (when showing next reward) */
    reposition: {
        /** Delay before repositioning */
        delay: 3000,
        /** Duration of reposition animation */
        duration: 500,
    },

    /** Next reward appearance timing */
    nextReward: {
        /** Delay before next reward appears */
        delay: 3750,
        /** Duration of appearance animation */
        duration: 750,
    },

    /** Crown count-up animation */
    crownCountUp: {
        /** Delay before counting starts */
        delay: 1000,
        /** Duration of count-up animation */
        duration: 2000,
    },
} as const

// ============================================================================
// CYCLE REWARDS BAR TIMINGS
// ============================================================================

export const CYCLE_REWARDS_BAR = {
    /** Total duration for tier transition animation sequence */
    totalDuration: 2000,

    /** Duration for asset flip animation */
    get assetFlipDuration() {
        return this.totalDuration / 1.5 // 1333ms
    },

    /** Delay before updating tier (at midpoint of asset flip) */
    get tierUpdateDelay() {
        return this.assetFlipDuration / 2 // 750ms
    },

    /** Delay before resetting to 0 progress */
    get resetDelay() {
        return this.assetFlipDuration // 1000ms
    },

    /** Small delay after reset before final update */
    get finalUpdateDelay() {
        return this.resetDelay + this.totalDuration * 0.05 // 1100ms
    },

    /** Pulse animation duration for progress changes */
    pulseDuration: 500,

    /** Delay before opening reward popup (for feedback is / 2) */
    rewardPopupDelay: 3000,

    /** Delay inside openRewardPopup function before showing dialog */
    rewardPopupOpenDelay: 2000,

    /** Duration of pulse text animation */
    get pulseTextDuration() {
        return this.totalDuration / 4 // 500ms
    },
} as const

// ============================================================================
// STEP PROGRESS SEQUENCE TIMINGS
// ============================================================================

export const STEP_PROGRESS = {
    /** Main timeout before showing step completion */
    mainTimeout: 3000,

    /** Delay after step progress before showing results popup */
    resultsPopupDelay: 1500,

    /** Delay after step progress before updating cycle progress */
    cycleProgressDelay: 3000,

    /** Polling interval for checking if solution is being shown */
    pollingInterval: 100,
} as const

// ============================================================================
// OTHER COMPONENT TIMINGS
// ============================================================================

export const COUNTDOWN_TEXT = {
    /** Callback delay after countdown ends */
    callbackDelay: 1500,
} as const

export const STEP_CHALLENGE_PROGRESS = {
    /** Fill appear animation duration */
    fillAppearDuration: 1500,
} as const

// ============================================================================
// DERIVED VALUES & HELPERS
// ============================================================================

/**
 * Legacy exports for backward compatibility
 * @deprecated Use named imports from timing constants above
 */
export const autoFeedTimeout = AUTO_FEED_TIMEOUT
export const autoMoveTimeout = AUTO_MOVE_BASE

/**
 * Get auto move delay based on move index
 * @param moveIndex - Index of the move (0 for first move)
 * @returns Timeout in milliseconds
 */
export function getAutoMoveDelay(moveIndex: number): number {
    return moveIndex === 0 ? AUTO_MOVE_EXTENDED : AUTO_MOVE_BASE
}

/**
 * Get reward popup sound delay based on whether it's the last day
 * @param isLastDay - Whether this is the last day of the cycle
 * @returns Delay in milliseconds
 */
export function getRewardPopupSoundDelay(isLastDay: boolean): number {
    return isLastDay ? REWARD_POPUP.soundEffectDelayLastDay : REWARD_POPUP.soundEffectDelayNormal
}
